import React, { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Navigation } from '@ucheba/ui/components/Navigation/desktop'
import {
  institutionItemSelectors,
  institutionItemThunks,
} from '@ucheba/store/institutions/item'
import { Container } from '@ucheba/ui/components/Container/desktop'
import institutionsItemThunks from '@ucheba/store/institutions/item/thunks'
import { chancesOfAdmissionThunk } from '@ucheba/store/institutions/chancesOfAdmission'
import { authSelectors } from '@ucheba/store/auth'
import { plannerPlanSelectors, plannerPlanThunks } from '@ucheba/store/planner/plan'
import { questionsSelectors } from '@ucheba/store/questions'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { EInstitutionType } from '@ucheba/store/institutions/item/types'
import { learningRequestThunk } from '@ucheba/store/institutions/learningRequest'
import { useNavigation } from '../bll/useNavigation'
import OfficialRepresentative from '../components/OfficialRepresentative/desktop'
import SelectEgeDialog from '../../SelectEgeDialog/desktop'
import { EFormType } from '../../UsersCommunication/types'
import {
  AdditionalChips,
  ChipContainer,
  FeedbackChip,
  LocationChip,
  StateChip,
} from '../components/ChipsInstitution'
import GalleryComponent from './GalleryComponent'
import { IContent, IInstitutionLayout } from '../types'
import ResultsAssessmentOfChances from '../components/AssessmentOfChances/ResultsAssessmentOfChances/desktop'
import CommunicationForm from '../../UsersCommunication/desktop/CommunicationForm'
import ButtonAssessmentOfChances from '../components/AssessmentOfChances/ButtonAssessmentOfChances'
import WantToStudy from '../components/WantToStudy/desktop'
import EducationalInstitutionAddedNotification from '../components/WantToStudy/components/EducationalInstitutionAddedNotification/desktop'
import { useWantToStudy } from '../components/WantToStudy/bll/useWantToStudy'
import {
  InstitutionLogo,
  OfficialRepresentativeWrapper,
  RightColumn,
  StickyContainer,
} from './styled'
import { isPaidPackage } from '../../../pages/uz/ssr/[id]/utils/isPaidPackage'
import AdmissionInstructionButton from '../../../pages/uz/components/AdmissionInstructionButton/desktop'
import { useProgramsOfficialRepresentative } from '../../../pages/program/ssr/[id]/index/bll/useProgramsOfficialRepresentative'
import { getNameInstitution } from '../../../../utils/helpers/getNameInstitution'
import { useLayoutInstitutionCore } from '../bll'
import { useButtonAssessmentOfChances } from '../components/AssessmentOfChances/ButtonAssessmentOfChances/bll'

const Content = memo(({ children }: IContent) => {
  return (
    <GridCol uxs={16} lg={18} as='article'>
      {children}
    </GridCol>
  )
})

const InstitutionLayout: FC<IInstitutionLayout> = (props) => {
  const {
    children,
    showRepresentative = true,
    showButtons = true,
    questionsListLimit,
    questionsListOffset,
    onQuestionAfterSubmit,
  } = props

  const stickyContainerRef = useRef<any>()
  const { route, query, replace, asPath } = useRouter()
  const isAuth = useSelector(authSelectors.isAuth)

  const { sections, activeIndex } = useNavigation()

  const dispatch = useDispatch()

  const data = useSelector(institutionItemSelectors.currentInstitution)

  useEffect(() => {
    if (isAuth) {
      const isAdmissionPlanner = [
        EInstitutionType.College,
        EInstitutionType.University,
        EInstitutionType.TrainingCenter,
      ].includes(data.type.value)

      if (isAdmissionPlanner) {
        dispatch<any>(plannerPlanThunks.getUzIds({}))
      } else {
        dispatch<any>(learningRequestThunk.fetchLearningRequestsIds({}))
      }

      dispatch(
        institutionItemThunks.fetchShortChancesOfAdmission({
          data: {
            referId: query.id,
            type: 'institution',
          },
        })
      )
    }
  }, [isAuth])

  const questions = useSelector(questionsSelectors.entity)

  const {
    setOpenResultsAssessmentOfChances,
    restartAssessmentOfChances,
    setOpenSelectEge,
    openSelectEge,
    openResultsAssessmentOfChances,
  } = useLayoutInstitutionCore()

  const hasBudgetPlaces = useMemo(() => {
    return !!data.currentAverageSummary?.budgetPlacesCount
  }, [data])

  const handleToggleSelectEge = useCallback(() => {
    setOpenSelectEge((prevValue) => !prevValue)

    if (query.showOddsStep1) {
      replace(
        stringifyUrl(asPath, {
          exclude: ['showOddsStep1'],
        }),
        undefined,
        {
          shallow: true,
        }
      )
    }
  }, [])

  const handleToggleResultsAssessmentOfChances = useCallback(
    () => setOpenResultsAssessmentOfChances((prevValue) => !prevValue),
    []
  )

  const handleAssessmentOfChances = async (value): Promise<void> => {
    const { achievements: achievementIds, egeList, egeExtraExam } = value

    const scores = egeList.map(({ examId, score }) => ({ examId: examId[0], score }))

    setOpenResultsAssessmentOfChances(true)
    setOpenSelectEge(false)

    await dispatch<any>(
      chancesOfAdmissionThunk.fetchChancesAdmission({
        data: {
          type: 'institution',
          referId: data.id,
          readyToExam: !!egeExtraExam.length,
          scores,
          achievementIds,
        },
      })
    )

    await dispatch<any>(
      institutionsItemThunks.fetchShortChancesOfAdmission({
        data: {
          type: 'institution',
          referId: data.id,
        },
      })
    )
  }

  const {
    isOpen,
    handleOpenDialog,
    onClose: onCloseForm,
    scrollToBlock,
  } = useProgramsOfficialRepresentative()

  useEffect(() => {
    if (query.showOddsStep1 && isAuth) {
      restartAssessmentOfChances()
    }
  }, [])

  const isMainUzPage = route === '/uz/[id]'

  const institutionId = data.id

  const uzInPlanIds = useSelector(plannerPlanSelectors.entityUzIds)

  const { handleWantToStudy, isInPlanOfAdmission, openCardAdded, handleToggleCardAdded } =
    useWantToStudy({
      id: institutionId,
      ids: uzInPlanIds || [],
    })

  const { currentProbability } = useButtonAssessmentOfChances(
    handleToggleResultsAssessmentOfChances
  )

  const calcSpace = useCallback(
    (num = 0) => {
      if (data.admissionInstruction && num === 0) {
        return 64
      }
      if (data.admissionInstruction && num < 51) {
        return 100
      }
      if (data.admissionInstruction && num >= 51) {
        return 110
      }
      if (num >= 51) {
        return 135
      }
      if (num > 0 && num < 51) {
        return 120
      }
      return 64
    },
    [data.admissionInstruction]
  )

  const officialRepresentativeWrapperMargin = useMemo(() => {
    let space = 0

    if (showButtons) {
      space += calcSpace(currentProbability)

      if (hasBudgetPlaces) {
        space += calcSpace(currentProbability)
      }

      if (data.admissionInstruction) {
        space += calcSpace(currentProbability)
      }
    }

    return space
  }, [
    showButtons,
    calcSpace,
    currentProbability,
    hasBudgetPlaces,
    data.admissionInstruction,
  ])

  const hasOfficialRepresentative = useMemo(() => {
    return (
      data?.officialRepresentative &&
      showRepresentative &&
      isPaidPackage(data.paidPackage)
    )
  }, [data, showRepresentative])

  const showLidForm = useMemo(() => {
    // если это МГУ, это показываем только хим факу МГУ
    if (data?.parent?.id === 1153 && institutionId !== 51731) {
      return false
    }

    return (
      Number(institutionId) !== 57067 &&
      Number(institutionId) !== 5908 &&
      data?.parent?.id !== 5908
    )
  }, [institutionId, data])

  const alt = useMemo(() => {
    let text = ''

    if (data?.abbreviation) {
      text += `${data.abbreviation}, `
    }

    if (data?.name) {
      text += data.name
    }

    return text
  }, [data])

  const isShowAdmissionInstruction = useMemo(() => {
    if (data.admissionInstruction) {
      return asPath.split('/').length === 3
    }

    return false
  }, [asPath, data.admissionInstruction])

  if (!data) return <span>404</span>

  const { isState, logo, mediaList, feedbackStats, type } = data

  const parentUzName = data.parent?.name
  const typeInstitution = getNameInstitution(data.type.value)
  const locationName = data.location?.name

  return (
    <>
      <Container>
        <Spacing spacing='xlarge' />

        <Grid spacing='large' justifyContent='space-between'>
          <GridCol uxs={20}>
            {parentUzName && (
              <a href={`/uz/${data.parent?.id}`} rel='noreferrer'>
                <Text
                  fontSize='h5'
                  fontWeight='regular'
                  lineHeight='h5'
                  color='blue'
                  html={false}
                >
                  {parentUzName}
                </Text>

                <Spacing spacing='uxsmall' />
              </a>
            )}

            <Heading
              as={isMainUzPage ? 'h1' : 'div'}
              spacing='small'
              h1={parentUzName ? data.abbreviation || data.name : data.name}
            />

            <Spacing spacing='medium' />

            <ChipContainer>
              <FeedbackChip feedbackStats={feedbackStats} />

              <LocationChip locationName={locationName} />

              <StateChip isState={isState} typeInstitution={typeInstitution} />

              {isPaidPackage(data.paidPackage) &&
                data.additionalBrandingTags &&
                data.additionalBrandingTags.length > 0 && (
                  <AdditionalChips tags={data.additionalBrandingTags} />
                )}
            </ChipContainer>
          </GridCol>

          {(logo?.fallbackUrl || logo?.webp) && (
            <GridCol uxs={4}>
              <InstitutionLogo
                alt={alt}
                fallbackUrl={logo?.fallbackUrl}
                webpUrl={logo?.webp}
              />
            </GridCol>
          )}
        </Grid>

        <Spacing spacing='xlarge' />

        {mediaList.length > 2 && data.paidPackage && (
          <>
            <GalleryComponent mediaList={mediaList} />

            <Spacing spacing='xlarge' />
          </>
        )}

        <Navigation items={sections} activeIndex={activeIndex} scroll={false} />

        <Spacing spacing='xlarge' />

        {children && (
          <Grid justifyContent='space-between'>
            <Content>{children}</Content>

            <RightColumn
              uxs={6}
              lg={5}
              paddingBottom={hasOfficialRepresentative ? '368px' : '0'}
              paddingTop={`${officialRepresentativeWrapperMargin + 16}px`}
            >
              {showButtons && (
                <StickyContainer
                  ref={stickyContainerRef}
                  space={`-${officialRepresentativeWrapperMargin}px`}
                >
                  <div>
                    <WantToStudy
                      institutionId={data.id}
                      isInPlanOfAdmission={isInPlanOfAdmission}
                      institutionType={data.type.value}
                      handleWantToStudy={handleWantToStudy}
                      block
                      alternativeButtonBlock
                      buttonText='Хочу поступить'
                    />

                    {hasBudgetPlaces && (
                      <>
                        <Spacing spacing='small' />

                        <ButtonAssessmentOfChances
                          restartAssessmentOfChances={restartAssessmentOfChances}
                          openResultList={handleToggleResultsAssessmentOfChances}
                        />
                      </>
                    )}

                    {isShowAdmissionInstruction && (
                      <div>
                        <Spacing spacing='small' />

                        <AdmissionInstructionButton
                          institutionId={institutionId}
                          withIcon
                        />
                      </div>
                    )}
                  </div>
                </StickyContainer>
              )}

              <Spacing spacing='small' />

              {hasOfficialRepresentative && (
                <OfficialRepresentativeWrapper space='-312px'>
                  <OfficialRepresentative
                    {...data?.officialRepresentative}
                    type={type.value}
                    handleOpenDialog={handleOpenDialog}
                  />
                </OfficialRepresentativeWrapper>
              )}
            </RightColumn>
          </Grid>
        )}
      </Container>

      <SelectEgeDialog
        open={openSelectEge}
        onClose={handleToggleSelectEge}
        onBeforeSubmit={handleAssessmentOfChances}
        overflow='hidden'
        scrollY
      />

      <ResultsAssessmentOfChances
        isInPlanOfAdmission={isInPlanOfAdmission}
        handleWantToStudy={handleWantToStudy}
        insitutionName={data.abbreviation}
        open={openResultsAssessmentOfChances}
        onClose={handleToggleResultsAssessmentOfChances}
        handleOpenDialog={handleOpenDialog}
        restartAssessmentOfChances={restartAssessmentOfChances}
        isPaidPackage={isPaidPackage(data.paidPackage)}
        showLidForm={showLidForm}
      />

      <CommunicationForm
        parentId={null}
        onClose={onCloseForm}
        formType={EFormType.questions}
        isOpen={isOpen}
        scrollTo={scrollToBlock}
        list={questions?.items || []}
        rootId={institutionId}
        type='institution'
        limit={questionsListLimit}
        offset={questionsListOffset}
        onAfterSubmit={onQuestionAfterSubmit}
      />

      <EducationalInstitutionAddedNotification
        lsKey='educationalInstitutionAddedNotification'
        open={openCardAdded}
        title='Учебное заведение добавлено <br/> в план поступления'
        onClose={handleToggleCardAdded}
      />
    </>
  )
}

export default memo(InstitutionLayout)
